import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { useValidationRules } from 'hooks/useValidationRules';

import { ReactComponent as CreditCardIcon } from 'assets/icons/credit-card.svg';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { CardMask } from 'components/shared/CardMask';
import { IconedPopup } from 'components/UI/IconedPopup';

import { CvvInput } from './submodules/CvvInput';

import css from './cvvPopup.module.sass';

interface Props {
  price: string;
  cardMask?: string;
  error?: null | string;
  isSubmitting?: boolean;
  onSubmit: ({ cvv }: { cvv: string }) => void;
  onClose?: () => void;
}

export const CvvPopup: React.FC<Props> = ({
  cardMask,
  price,
  error,
  isSubmitting,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation('common');
  const {
    control,
    handleSubmit,
    errors: formErrors,
  } = useForm({
    reValidateMode: 'onChange',
    defaultValues: {
      cvv: '',
    },
  });
  const { requiredValidation } = useValidationRules();

  const handleClose = () => {
    !isSubmitting && onClose && onClose();
  };

  return (
    <IconedPopup
      icon={<CreditCardIcon className={css.icon} />}
      onClose={handleClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="cvv"
          as={CvvInput}
          control={control}
          rules={{
            ...requiredValidation,
            maxLength: {
              value: 3,
              message: t('validations.cvvLength'),
            },
            minLength: {
              value: 3,
              message: t('validations.cvvLength'),
            },
          }}
        />

        {(formErrors.cvv?.message || error) && (
          <p className={css.error}>{formErrors.cvv?.message || error}</p>
        )}

        {cardMask && <CardMask mask={cardMask} className={css.footer} />}

        <BaseButton
          nativeType="submit"
          type={ButtonTypes.Accent}
          loading={isSubmitting}
          className={css.submit}
        >
          {t('continue')}
        </BaseButton>

        <footer className={css.footer}>
          <Trans
            i18nKey="cvvPopup.price"
            ns="common"
            values={{
              cost: price,
            }}
          />
        </footer>
      </form>
    </IconedPopup>
  );
};
